import React from "react";
import { SolvencyResult } from "../types";
import AdviceBlock from "./AdviceBlock";
import { CheckCircle, XCircle, FileText, Sparkles, TrendingDown, RefreshCw } from "lucide-react";

interface Props {
  data: SolvencyResult;
  // «Подробнее у ассистента» в рекомендациях — без колбэка кнопка не показывается.
  onAskAssistant?: (question: string) => void;
}

export default function SolvencyTab({ data, onAskAssistant }: Props) {
  // Convert object metrics to render list
  const metrics = Object.entries(data.details).map(([key, item]) => ({
    code: key.toUpperCase(),
    ...item
  }));

  // Норматив приходит текстом («> 2.0», «0.2 - 0.25», «≥ 0.1»), иногда вовсе не
  // числом («снижение в динамике» — Л5). Разбираем на порог, чтобы нарисовать бар;
  // то, что не разбирается, — просто без бара (как было).
  const parseNorm = (norm: string): { threshold: number; max?: number } | null => {
    const range = norm.match(/([\d.]+)\s*-\s*([\d.]+)/);
    if (range) return { threshold: parseFloat(range[1]), max: parseFloat(range[2]) };
    const single = norm.match(/[<>≤≥]=?\s*([\d.]+)/);
    if (single) return { threshold: parseFloat(single[1]) };
    return null;
  };

  const normBar = (row: { norm: string; value: number; status: "ok" | "fail" }) => {
    const parsed = parseNorm(row.norm);
    if (!parsed) return null;
    const ceiling = parsed.max ?? parsed.threshold * 1.5;
    const pct = Math.max(0, Math.min(100, (row.value / ceiling) * 100));
    const color = row.status === "ok" ? "bg-emerald-500" : "bg-rose-500";
    return (
      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden shrink-0">
        <div style={{ width: `${pct}%` }} className={`h-full rounded-full transition-all duration-500 ${color}`} />
      </div>
    );
  };

  // Resolve status badges
  const getStatusBadge = (status: 'ok' | 'fail') => {
    switch (status) {
      case 'ok':
        return (
          <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200/50 text-emerald-800 t-meta font-mono font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
            Норма
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 bg-rose-50 border border-rose-250/30 text-rose-800 t-meta font-mono font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">
            <XCircle className="w-3.5 h-3.5 text-rose-500" />
            Отклонение
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Solvency Title / Header banner */}
      <div className="bg-gradient-to-r from-slate-900 to-indigo-950 text-white p-6 rounded-2xl shadow-md border border-slate-800 space-y-2.5">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-indigo-400" />
          <span className="text-[10px] uppercase tracking-widest font-mono font-bold text-indigo-300">Методика оценки платежеспособности (Л1-Л9)</span>
        </div>
        <h3 className="text-xl md:text-2xl font-extrabold font-display tracking-tight text-white">Анализ ликвидности баланса и восстановления платёжных сил</h3>
        <p className="text-xs text-slate-350 max-w-3xl leading-relaxed font-medium">
          Тестирует баланс на абсолютную, быструю и текущую форму ликвидности, обеспеченность СОС, а также предлагает математический прогноз восстановления или утраты потенциала погашения долгов на 3 и 6 месяцев по регламентам СЭБ Anyskin.
        </p>
      </div>

      {/* Grid Table representation */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md">
        {/* Десктоп: таблица. На мобиле скрыта в пользу карточек ниже. */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-slate-450 text-[10px] font-mono font-bold uppercase tracking-wider">
                <th className="py-3 px-6">Код</th>
                <th className="py-3 px-4">Показатель и экономический смысл</th>
                <th className="py-3 px-4 text-center">Норматив</th>
                <th className="py-3 px-4 text-center">Значение</th>
                <th className="py-3 px-6 text-center">Статус</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {metrics.map((row) => (
                <tr key={row.code} className="hover:bg-indigo-50/10 transition-colors group">
                  <td className="py-3.5 px-6 font-mono text-xs font-black text-indigo-700">
                    <span className="bg-indigo-50 border border-indigo-150/40 px-2 py-1 rounded">
                      {row.code}
                    </span>
                  </td>
                  <td className="py-3.5 px-4 font-bold text-slate-800 tracking-tight text-xs md:text-sm">
                    {row.label}
                  </td>
                  <td className="py-3.5 px-4 text-center font-mono text-xs text-slate-500 font-bold bg-slate-50/40">
                    {row.norm}
                  </td>
                  <td className="py-3.5 px-4 text-center font-mono text-sm font-black text-slate-950">
                    <div className="flex items-center justify-center gap-2">
                      <span>{row.value} <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">коэф.</span></span>
                      {normBar(row)}
                    </div>
                  </td>
                  <td className="py-3.5 px-6 text-center whitespace-nowrap">
                    {getStatusBadge(row.status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Мобила: каждый показатель — карточка. Формула (admin-only, тех. деталь) опущена. */}
        <div className="md:hidden divide-y divide-slate-100">
          {metrics.map((row) => (
            <div key={row.code} className="p-4 space-y-2.5">
              <div className="flex items-start justify-between gap-3">
                <span className="font-bold text-slate-800 text-sm leading-snug tracking-tight">{row.label}</span>
                <span className="font-mono text-xs font-black text-indigo-700 bg-indigo-50 border border-indigo-150/40 px-2 py-1 rounded shrink-0">
                  {row.code}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="font-mono text-lg font-black text-slate-950">{row.value}</span>
                  <span className="t-micro font-mono text-slate-400 truncate">норматив: {row.norm}</span>
                  {normBar(row)}
                </div>
                <span className="shrink-0">{getStatusBadge(row.status)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Conclusions Drawer and Recommendations card */}
      <div className="bg-slate-50/80 p-6 rounded-2xl border border-slate-100 space-y-4 shadow-sm">
        <h4 className="font-extrabold text-slate-900 flex items-center gap-2 font-display text-base">
          <Sparkles className="w-5 h-5 text-indigo-700" />
          Выводы и рекомендации на основе отклонений
        </h4>
        <div className="text-sm text-slate-700 space-y-3 leading-relaxed">
          <div className="bg-white p-4 rounded-xl border border-slate-100 font-bold text-slate-800 shadow-xs text-xs md:text-sm leading-relaxed border-l-4 border-l-indigo-600">
            {data.interpretation}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div className="p-4 bg-white rounded-xl border border-slate-100 flex gap-3 items-start transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-150">
              <RefreshCw className="w-5 h-5 text-indigo-550 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-extrabold text-xs text-slate-900 block font-display tracking-tight">Фактор восстановления (6 месяцев)</span>
                <p className="text-xs text-slate-500 leading-snug font-medium">
                  Если значение Л8 &lt; 1.0, это свидетельствует о деструктивной финансовой динамике: компания не сможет восстановить текущую ликвидность до нормативов в течение полугода на основе текущего тренда.
                </p>
              </div>
            </div>

            <div className="p-4 bg-white rounded-xl border border-slate-100 flex gap-3 items-start transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-150">
              <TrendingDown className="w-5 h-5 text-indigo-550 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-extrabold text-xs text-slate-900 block font-display tracking-tight">Фактор утраты платежеспособности (3 месяца)</span>
                <p className="text-xs text-slate-500 leading-snug font-medium">
                  Если Л9 &lt; 1.0, существует высокий риск потери возможности своевременно и в полном объеме закрывать обязательства в течение ближайшего квартала, даже если текущий баланс формально выглядит ликвидным.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdviceBlock conclusion={data.conclusion} recommendations={data.recommendations} onAskAssistant={onAskAssistant} />
    </div>
  );
}
